//* Third Party Libraries
import classNames from "classnames";

//* Local or Custom Libraries
import { useUserCountry } from "../../hooks/useLocale";
import { LoadingIndicator, Modal } from "@lululemon/ui-library";
import BopisModalHeader from "./components/bopis-modal-header";
import BopisModalSearch from "./components/bopis-modal-search";
import BopisModalFooter from "./components/bopis-modal-footer";
import BopisModalStoreResults from "./components/bopis-modal-store-results";
import BopisSearchQueryMessage from "./components/bopis-search-message-query";
import { userDenyGeolocationMessage } from "./utilities/constants";

//* Importing Styles
import styles from "./bopis-modal.module.scss";

/**
 * @param {object} activeSku - an object that contains description of the current product
 * @param {object} activeStoreRadioBtn - an object containing id and name of the current selected store
 * @param {function} getAddressFromSearchQuery - a function that fetches the address when user search for a location
 * @param {object} getUserAddressError - an error set from the getCurrentPosition or geocodeCoordinates if no location could be found
 * @param {function} handleGetUserAddress - a function to fetch the exact location of an user
 * @param {boolean} hasCookieError - a boolean that determines if there's an error related to cookies
 * @param {boolean} isLoadingUserAddress - a boolean indicating if the user's address is being fetched
 * @param {boolean} isLoadingUserSearchQuery - a boolean indicating if the user's search query is being processed
 * @param {array} nearbyBopisStores - an array that contains all neaby stores
 * @param {array} nearbyStoresWithInventoryButNoBopis - an array of nearby stores that have inventory but do not support BOPIS
 * @param {function} onCloseModal - a function that cleans the variables when modal is closed
 * @param {function} onSelectStoreClickHandler - a function that handles the store selection radio button click
 * @param {string|undefined} productImage - a string with URL of the product image displayed in modal
 * @param {string|undefined} productName - a string with the name of the product displayed in modal
 * @param {object} searchQueryError - an error set from the geocodeAddress function if no address could be found
 * @param {object} selectedStore - an object that contains id and name of selected store
 * @param {function} setActiveStoreRadioBtn - the setter for the activeStoreRadioBtn state in the connector
 * @param {function} setUserSearchedLocation - the setter for the userSearchedLocation state in `userNearbyStores`
 * @param {function|undefined} setDeliveryToShip - a function that changes delivery type to SHP
 * @param {function} setGetUserAddressError - a function to set the error when fetching the user address
 * @param {boolean} shouldDisplayProductInfoHeader - a boolean that determines if header needs to show or not
 * @param {object} userSearchedLocation - the userSearchedLocation state in `userNearbyStores`
 * @param {object} userLocation - an object that contains data of user location
 * @param {string|undefined} userSearchedQuery - a string containing the last searched location query input by the user
 * @typedef {Object} flags - Represents a collection of feature flags
 * @property {Object.<string, boolean} ld - An object containing LaunchDarkly flags
 * @property {Object.<string, boolean} ab - An object containing A/B testing flags
 * @example Example usage:
 * const flags = {
 *   ld: {
 *     showBetaFeature: false
 *   },
 *   ab: {
 *     pdpShipBOPISRedesign: true,
 *   }
 * };
 * @param {object} maxWidths - an object that contains the max widths of the device
 * @param {number} distanceFilter - The current distance filter value, stored as a numeric state, this value is derived from a Jotai atom
 * @param {function} setDistanceFilter - A callback function to update the distance filter value, this value is derived from a Jotai atom
 */

const BopisModal = ({
  activeSku,
  activeStoreRadioBtn,
  getAddressFromSearchQuery,
  getUserAddressError,
  handleGetUserAddress,
  hasCookieError,
  isLoadingUserAddress,
  isLoadingUserSearchQuery,
  nearbyBopisStores,
  nearbyStoresWithInventoryButNoBopis,
  onCloseModal,
  onSelectStoreClickHandler,
  productImage,
  productName,
  searchQueryError,
  selectedStore,
  setActiveStoreRadioBtn,
  setDeliveryToShip,
  setUserSearchedLocation,
  setGetUserAddressError,
  shouldDisplayProductInfoHeader,
  userLocation,
  userSearchedLocation,
  userSearchedQuery,
  flags,
  maxWidths,
  distanceFilter,
  setDistanceFilter
}) => {
  const userCountry = useUserCountry();
  const pdpShipBOPISRedesign = flags?.ab?.pdpShipBOPISRedesign || false;

  const showNearbyBopisStoreResults = () => {
    const hasAddress = userLocation?.address || userSearchedLocation?.address;
    const isLoading = isLoadingUserAddress || isLoadingUserSearchQuery;

    if (hasCookieError && !hasAddress) return null;

    if (nearbyBopisStores === null || isLoading) {
      return (
        <div className={styles.loaderContainer}>
          <LoadingIndicator color="gray" className={styles.loadingIndicator} />
        </div>
      );
    }

    if (searchQueryError) {
      return (
        <BopisSearchQueryMessage
          setDeliveryToShip={setDeliveryToShip}
          searchQueryError={searchQueryError}
          userCountry={userCountry}
        />
      );
    }

    return (
      <BopisModalStoreResults
        activeStoreRadioBtn={activeStoreRadioBtn}
        nearbyBopisStores={nearbyBopisStores}
        nearbyStoresWithInventoryButNoBopis={
          nearbyStoresWithInventoryButNoBopis
        }
        setActiveStoreRadioBtn={setActiveStoreRadioBtn}
        setDeliveryToShip={setDeliveryToShip}
        searchQueryError={searchQueryError}
        selectedStore={selectedStore}
        userCountry={userCountry}
        userLocation={userLocation}
        userSearchedLocation={userSearchedLocation}
        userSearchedQuery={userSearchedQuery}
        distanceFilter={distanceFilter}
        setDistanceFilter={setDistanceFilter}
      />
    );
  };

  return (
    <Modal
      data-testid="bopis-modal-rebuild"
      aria-label="Store pickup availability"
      visible={true}
      classes={{
        container: styles.bopisContainer,
        modal: styles.bopisModal
      }}
      fullScreenMobile={true}
      onRequestClose={onCloseModal}
    >
      <div
        className={classNames(styles.productContainer, "OneLinkTx", {
          [styles.productContainerHeight]:
            userLocation?.address || userLocation?.city,
          [styles.productContainerNewBopis]: pdpShipBOPISRedesign
        })}
      >
        {shouldDisplayProductInfoHeader && (
          <BopisModalHeader
            activeSku={activeSku}
            productImage={productImage}
            productName={productName}
            maxWidths={maxWidths}
          />
        )}
        <BopisModalSearch
          activeStoreRadioBtn={activeStoreRadioBtn}
          getAddressFromSearchQuery={getAddressFromSearchQuery}
          handleGetUserAddress={handleGetUserAddress}
          setUserSearchedLocation={setUserSearchedLocation}
          getUserAddressError={getUserAddressError}
          setActiveStoreRadioBtn={setActiveStoreRadioBtn}
          setGetUserAddressError={setGetUserAddressError}
          userLocation={userLocation}
          userCountry={userCountry}
        />
        {getUserAddressError && (
          <div className={styles.denyGeolocationMessage}>
            {userDenyGeolocationMessage}
          </div>
        )}
        {showNearbyBopisStoreResults()}
        <BopisModalFooter
          activeStoreRadioBtn={activeStoreRadioBtn}
          isLoadingUserAddress={isLoadingUserAddress}
          isLoadingUserSearchQuery={isLoadingUserSearchQuery}
          nearbyBopisStores={nearbyBopisStores}
          onSelectStoreClickHandler={onSelectStoreClickHandler}
          searchQueryError={searchQueryError}
          selectedStore={selectedStore}
          pdpShipBOPISRedesign={pdpShipBOPISRedesign}
        />
      </div>
    </Modal>
  );
};

export default BopisModal;
